import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Input,
  Stack,
  Typography,
} from '@mui/joy';
import { directSupabase } from '../lib/supabaseClient';
import { customerService, type Customer } from '../services/customerService';
import type { Project } from '../types/project';
import JobCard from './project-workspace/JobCard';
import { applyWorkflowTaskChange, getFullWorkflowTasksForMode, getWorkflowFlagPatch } from '../utils/workflowUtils';
import { areAllProjectJobCardsComplete, deleteJobCard, getJobCard } from './project-workspace/jobCard.service';
import type { JobCardRecord } from './project-workspace/jobCard.types';
import { formatDayRanges, formatInstallStamp, londonDayKey } from './project-workspace/jobCardInstallDate';
import { loadOpenJobCards } from './project-workspace/openJobCards.service';import {
  formatCompletionStatus,
  sectionOpenJobCards,
  type OpenJobCard,
  type OpenJobCardSectionId,
} from './project-workspace/openJobCards.model';

function daysOpen(raisedAt: string, now = new Date()): number {
  const from = londonDayKey(raisedAt);
  const to = londonDayKey(now.toISOString());
  return Math.max(Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86400000), 0);
}

function raisedLabel(card: OpenJobCard): string {
  const date = new Date(card.raisedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' });
  if (card.completion) return `Raised ${date}`;
  const days = daysOpen(card.raisedAt);
  return `Raised ${date} · ${days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} open`}`;
}

function installLabel(card: OpenJobCard): string {
  if (!card.installDays.length) return 'Date pending';
  if (card.installDays.length === 1) return formatInstallStamp(card.installStart) || formatDayRanges(card.installDays);
  return `${formatDayRanges(card.installDays)} (${card.installDays.length} days)`;
}

const SECTION_COLOR: Record<OpenJobCardSectionId, 'danger' | 'primary' | 'neutral' | 'warning' | 'success'> = {
  overdue: 'danger',
  installed: 'success',
  this_week: 'primary',
  next_week: 'neutral',
  later: 'neutral',
  no_date: 'warning',
};

function matches(card: OpenJobCard, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [card.lifecycleRef, card.workspace, card.customer, card.title, card.siteAddress, ...card.crew]
    .some((value) => String(value || '').toLowerCase().includes(needle));
}

type Expanded = {
  jobCard: JobCardRecord;
  project: Project;
  customer: Customer | null;
};

function ExpandedJobCard({
  row,
  onChanged,
}: {
  row: OpenJobCard;
  onChanged: () => Promise<void>;
}) {
  const [state, setState] = useState<Expanded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState(null);
    setError(null);
    void (async () => {
      try {
        const [jobCard, projectResult] = await Promise.all([
          getJobCard(row.id),
          directSupabase.from('projects').select('*').eq('id', row.projectId).single(),
        ]);
        if (!jobCard) throw new Error('Job card not found');
        if (projectResult.error) throw new Error(projectResult.error.message);
        const project = projectResult.data as Project;
        const customer = project.customer_id
          ? await customerService.getLocalCustomerById(String(project.customer_id))
          : null;
        if (!cancelled) setState({ jobCard, project, customer });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load job card');
      }
    })();
    return () => { cancelled = true; };
  }, [row.id, row.projectId]);

  if (error) return <Alert color="danger">{error}</Alert>;
  if (!state) return <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size="sm" /></Stack>;

  const { project } = state;
  const patchWorkflowComplete = async (complete: boolean) => {
    const tasks = getFullWorkflowTasksForMode(project.workflow_tasks || [], project.supply_only || false, project);
    let updated = applyWorkflowTaskChange(tasks, 'Installation Complete', complete);
    updated = applyWorkflowTaskChange(updated, 'Request Review', complete);
    const flags = getWorkflowFlagPatch(updated, project.supply_only || false, project);
    const { error: updateError } = await directSupabase
      .from('projects')
      .update({
        workflow_tasks: updated,
        supplier_quote_received: flags.supplier_quote_received,
        deposit_received: flags.deposit_received,
        ordered: flags.ordered,
      })
      .eq('id', project.id);
    if (updateError) throw new Error(updateError.message);
    setState((current) => (current ? { ...current, project: { ...current.project, workflow_tasks: updated } } : current));
  };

  return (
    <JobCard
      jobCard={state.jobCard}
      project={state.project}
      customer={state.customer}
      isOldest={row.isOldestOnProject}
      allowAssign
      onAssigneesChanged={() => { void onChanged(); }}
      onAttestedComplete={async () => {
        if (await areAllProjectJobCardsComplete(project.id)) await patchWorkflowComplete(true);
        await onChanged();
      }}
      onReleased={async () => {
        await patchWorkflowComplete(false);
        await onChanged();
      }}
      onProjectPatch={async (patch) => {
        const { error: updateError } = await directSupabase.from('projects').update(patch).eq('id', project.id);
        if (updateError) throw new Error(updateError.message);
        setState((current) => (current ? { ...current, project: { ...current.project, ...patch } } : current));
        await onChanged();
      }}
      onJobCardPatch={(next) => {
        setState((current) => (current ? { ...current, jobCard: next } : current));
        void onChanged();
      }}
      onDeleteCard={async () => {
        await deleteJobCard(row.id);
        await onChanged();
      }}
    />
  );
}

const OpenJobCardsPage: React.FC = () => {
  const [cards, setCards] = useState<OpenJobCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setCards(await loadOpenJobCards());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job cards');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void refresh().finally(() => setLoading(false));
  }, [refresh]);

  const sections = useMemo(
    () => sectionOpenJobCards(cards.filter((card) => matches(card, search))),
    [cards, search],
  );
  const shown = sections.reduce((sum, section) => sum + section.cards.length, 0);

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 }, width: '100%', maxWidth: 1200, mx: 'auto', boxSizing: 'border-box' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} gap={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography level="h3">Job Cards</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Every job card raised, in install date order. Cards marked complete stay here until the workspace is closed.
          </Typography>
        </Box>
        <Input
          size="sm"
          placeholder="Search job ref, workspace, customer or crew"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: { sm: 320 } }}
        />
      </Stack>

      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size="sm" /></Stack>
      ) : shown === 0 ? (
        <Card variant="outlined">
          <Typography level="body-sm" sx={{ color: 'neutral.500', textAlign: 'center', py: 2 }}>
            {search.trim() ? 'No job cards match that search.' : 'No job cards to show.'}
          </Typography>
        </Card>
      ) : (
        <Stack gap={2}>
          {sections.filter((section) => section.cards.length > 0).map((section) => (
            <Box key={section.id}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
                <Typography level="title-md">{section.title}</Typography>
                <Chip size="sm" variant="soft" color={SECTION_COLOR[section.id]}>{section.cards.length}</Chip>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{section.hint}</Typography>
              </Stack>
              <Card variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
                {section.cards.map((card, index) => {
                  const expanded = expandedId === card.id;
                  return (
                    <Box
                      key={card.id}
                      sx={{ borderTop: index === 0 ? 'none' : '1px solid', borderColor: 'divider' }}
                    >
                      <Box
                        role="button"
                        tabIndex={0}
                        aria-expanded={expanded}
                        onClick={() => setExpandedId(expanded ? null : card.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setExpandedId(expanded ? null : card.id);
                          }
                        }}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '200px minmax(0, 1fr) 200px 220px auto' },
                          gap: { xs: 0.5, md: 2 },
                          alignItems: 'center',
                          px: 2,
                          py: 1.25,
                          cursor: 'pointer',
                          bgcolor: expanded ? 'background.level1' : 'transparent',
                          '&:hover': { bgcolor: 'background.level1' },
                        }}
                      >
                        <Box>
                          <Typography level="title-sm" color={section.id === 'overdue' ? 'danger' : undefined}>
                            {installLabel(card)}
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{raisedLabel(card)}</Typography>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography level="title-sm" noWrap>
                            {card.customer || card.workspace}
                            {card.lifecycleRef && (
                              <Typography component="span" level="body-xs" sx={{ color: 'neutral.500', fontWeight: 500, ml: 1 }}>
                                {card.lifecycleRef}
                              </Typography>
                            )}
                          </Typography>
                          {card.customer && <Typography level="body-xs" noWrap>{card.workspace}</Typography>}
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }} noWrap>
                            {[
                              card.title,
                              card.continuesFrom.length ? `continues ${card.continuesFrom.join(', ')}` : '',
                              card.siteAddress,
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        <Box>
                          {card.crew.length ? (
                            <Typography level="body-sm" noWrap>{card.crew.join(', ')}</Typography>
                          ) : (
                            <Chip size="sm" variant="soft" color="warning">No crew assigned</Chip>
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={card.completion?.kind === 'completed' ? 'success' : 'neutral'}
                            sx={{ maxWidth: '100%', whiteSpace: 'normal', '--Chip-minHeight': 'auto', py: 0.25 }}
                          >
                            {formatCompletionStatus(card.completion)}
                          </Chip>
                        </Box>
                        <Button
                          size="sm"
                          variant="plain"
                          component={RouterLink}
                          to={`/projects/${card.projectId}/workspace/implementation`}
                          onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        >
                          Open workspace
                        </Button>
                      </Box>
                      {expanded && (
                        <Box sx={{ px: { xs: 1, md: 2 }, pb: 2, pt: 1, bgcolor: 'background.level1', overflow: 'visible' }}>
                          <ExpandedJobCard row={card} onChanged={refresh} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Card>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default OpenJobCardsPage;
